import React, {
  useCallback, useMemo,
  useState,
} from 'react';
import moment from 'moment';
import {hooks} from 'farmx-redux-core';
import { useTranslation } from 'react-i18next';
import { TEMPERATURE } from 'components/hooks/graph';
import { getYminMax } from '../../helper/graphHelper';
import NewSensorDataChart from './NewSensorDataChart';
import { VAR_TEMPERATURE } from './constants';

interface Sensor {
  name?: string;
  id?: number;
  type?: string;
  identifier?: string;
}

interface TemperatureChartProps {
  sensor?: Sensor | any;
  startDate?: any;
  endDate?: any;
  cached?: string | any;
  updateYminmax?: () => void;
  uniformYAxis?: boolean;
  yMinMaxMap?: any;
  header?: boolean;
}

const { useUnits } = hooks;

const TemperatureChart: React.FC<TemperatureChartProps> = (props: any) => {
  const {
    sensor, startDate, endDate, cached, updateYminmax, uniformYAxis, yMinMaxMap,
    header
  } = props;
  const { t } = useTranslation();
  const getUserUnits = useUnits();
  const [label, setLabel] = useState('');

  const globalYMinMax = yMinMaxMap && yMinMaxMap[TEMPERATURE]
    ?yMinMaxMap[TEMPERATURE]:{min: null, max: null};

  const yMinMax = useMemo(() => {
    if((globalYMinMax.min || globalYMinMax.min===0)
        && globalYMinMax.max) { return { min: globalYMinMax.min, max: globalYMinMax.max}; }
    return null;
  }, [globalYMinMax.min, globalYMinMax.max]);

  const customConfig = useCallback((configObj, seriesData) => {
    if (configObj?.series?.[0]) {
      const yMinMax = getYminMax(seriesData) || { min: 0, max: 0};
      const { multiplier } = configObj?.series?.[0] || { multiplier: 1 };
      const { units } = seriesData?.temperature?.[0] || {};
      const fromUnit = units === '&deg;C' ? 'degrees_celsius' : 'degrees_fahrenheit';
      const data = configObj?.series?.[0].data;
      const {value: yMin, label} = getUserUnits(yMinMax.min * multiplier, fromUnit, 'temperature', {decimalPlaces: 3});
      const {value: yMax} = getUserUnits(yMinMax.max * multiplier, fromUnit, 'temperature', {decimalPlaces: 3});
      updateYminmax(TEMPERATURE, yMin, yMax);
      setLabel(label);
      return {
        ...configObj,
        series: [{
          ...configObj?.series?.[0],
          data,
          tooltip: {
            valueDecimals: 2,
            valueSuffix: ` ${label}`,
          },
          name: 'Temperature',
        }],
        yAxis: [{
          ...(configObj.yAxis[0] || {
            title: null,
            opposite: false,
            labels: {
              y: -1,
              x: 0,
              align: 'left'
            }
          }),
          min: yMin,
          max: yMax,
        }],
      };
    }

    return configObj;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateYminmax]);

  return (
    <>
      <NewSensorDataChart
        sensorName={sensor.name}
        sensorId={sensor.id}
        sensorType={sensor.type}
        sensorIdentifier={sensor.identifier}
        variables={VAR_TEMPERATURE}
        startDate={startDate}
        endDate={endDate}
        cached={cached}
        customConfig={customConfig}
        header={header && {title: t('Temperature'), units: t(label), right: []}}
        uniformYAxis={uniformYAxis}
        yMinMax={yMinMax}
      />
    </>
  );
};

TemperatureChart.defaultProps = {
  sensor: null,
  startDate: null,
  endDate: null,
  cached: null,
  updateYminmax: () => null,
  uniformYAxis: false,
  yMinMaxMap: null,
  header: true,
};

export default TemperatureChart;
